import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import './LineClamp.scss';

interface LineClampProps {
  children: string; // The actual text to be clamped
  disableLessMore?: boolean;
  moreText?: string; // the "Read more" literal when text is clamped.
  lessText?: string; // the "Read Less" literal when text is clamped.
  maxChars?: number; // Maximum allowed characters
  // If text is clamped, the characters of the remaining text should be at least this number of chars long to crop
  offsetToleranceChars?: number;
  smallText?: boolean;
}

const LineClamp = ({ children,
                     maxChars = 250,
                     moreText = 'More',
                     lessText = 'Less',
                     offsetToleranceChars = 20,
                     disableLessMore = false,
                     smallText = false
                   }:
    LineClampProps) => {
  const [ textExpanded, setTextExpanded ] = useState(false);

  const textReachedLimit = Boolean(children) && children.length >= maxChars
    && (children.length - maxChars) > offsetToleranceChars;

  // Computed synchronously (not in a useEffect) so SSR markup is already clamped, not just after client hydration.
  const modifiedText = useMemo(() => {
    if (!children) {
      return children;
    }

    if (!textReachedLimit || textExpanded) {
      return children.trim();
    }

    let modifiedTextTemp = children.trim().slice(0, maxChars);
    const lastSpaceIndex = modifiedTextTemp.lastIndexOf(' ');
    // Do not cut the words in half
    if (lastSpaceIndex !== -1) {
      modifiedTextTemp = modifiedTextTemp.slice(0, lastSpaceIndex);
    }

    // Add ellipsis
    return `${modifiedTextTemp} ..`;
  }, [children, maxChars, textExpanded, textReachedLimit]);

  if (children === undefined || children.length === 0) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  return (
    <div styleName="multiline-crop">
        <div styleName={
          classNames({
            'multiline-crop__text': true,
            'multiline-crop__text--open': textExpanded,
            'multiline-crop__text--small-text': smallText
          })}
        >
          {modifiedText}
          { !disableLessMore && textReachedLimit &&
            <span styleName="multiline-crop__more" onClick={() => setTextExpanded(!textExpanded)}>
              {textExpanded ? lessText : moreText}
            </span>
          }
        </div>
    </div>
  );
};

export default LineClamp;
